import { UserType, UserRoles, UserData } from '@/common/type';
import { find } from 'lodash';
import jsonata from 'jsonata';
import { getShopSetting } from './utils';

export default function(userData: UserData) {
  const defaultPrivateRole =
    userData && userData.type && [UserType.member, UserType.owner].includes(userData.type);
  const permissions: Record<string, (data: any) => boolean> = {};
  userData?.shop_roles_data?.map(({ name, effect = 'allow', condition = '$' }) => {
    permissions[name] = (data: any) => {
      let result = false;
      if (userData.shop_roles?.includes(UserRoles.admin)) {
        return true;
      }
      if (data) {
        (async () => {
          const expression = jsonata(condition);
          result = await expression.evaluate(data);
        })();
      } else {
        return effect === 'allow';
      }
      return effect === 'allow' ? result : !result;
    };
  });
  const featureContactExportRole = getShopSetting(
    userData,
    'feature_contact_export_role',
    'enabled',
  );
  return {
    // router filter
    isAdmin: (userData && userData?.shop_roles?.includes(UserRoles.admin)) || false,
    isCustomer: userData?.type && [UserType.customer].includes(userData.type),
    isForwarder:
      !!userData && !!userData.type && ![UserType.agent, UserType.customer].includes(userData.type),
    memberRouterFilter: defaultPrivateRole,
    accountingRouteFilter:
      defaultPrivateRole &&
      (userData.shop_roles.includes(UserRoles.view_accounting) ||
        userData.shop_roles.includes(UserRoles.admin)),
    reportRouteFilter:
      defaultPrivateRole &&
      (userData.shop_roles.includes(UserRoles.view_report) ||
        userData.shop_roles.includes(UserRoles.admin)),

    chargeItemRouteFilter:
      defaultPrivateRole &&
      (userData.shop_roles.includes(UserRoles.approve_charge_item) ||
        userData.shop_roles.includes(UserRoles.admin)),

    bankReconcileRouteFilter:
      defaultPrivateRole &&
      (userData.shop_roles.includes(UserRoles.able_bank_reconcile) ||
        userData.shop_roles.includes(UserRoles.admin)),

    adminRouteFilter: userData && userData.type && userData.shop_roles.includes(UserRoles.admin),
    featureExcelHeader: !!find(userData?.shop?.settings, {
      name: 'feature_excel_header',
      enabled: true,
    }),
    featureStatementExcelSyncVisibleColumns: !!find(userData?.shop?.settings, {
      name: 'feature_statement_excel_sync_visible_columns',
      enabled: true,
    }),
    featureMultiCurrencies: !!userData?.shop?.currencies && userData.shop.currencies.length > 1,
    featureAllowOverPaid: getShopSetting(userData, 'feature_allow_over_paid', 'enabled'),
    featureIncludeCodeInGLReport: getShopSetting(
      userData,
      'feature_include_code_in_gl_report',
      'enabled',
    ),
    featureARAPMainCurrencyColumn: getShopSetting(
      userData,
      'feature_arap_main_currency_column',
      'enabled',
    ),
    featureShipmentReportMultiCurrencies: getShopSetting(
      userData,
      'feature_shipment_report_multi_currencies',
      'enabled',
    ),
    featureShipmentReportSplitCurrencyExport: getShopSetting(
      userData,
      'feature_shipment_report_split_currency_export',
      'enabled',
    ),
    featureShipmentReportSplitDCNote: getShopSetting(
      userData,
      'feature_shipment_report_split_dc_note',
      'enabled',
    ),
    featureBatchDuplicate: getShopSetting(userData, 'feature_batch_duplicate', 'enabled'),
    defaultDuplicateCargoDetails: !!getShopSetting(
      userData,
      'feature_duplicate_cargo_details',
      'enabled',
    ),
    featureARAPCurrency: getShopSetting(userData, 'feature_arap_currency', 'enabled'),
    featureMultiLineChargeItemsInTable: getShopSetting(
      userData,
      'feature_multi_line_charge_items_in_table',
      'enabled',
    ),
    featureChargeItemCodeDisable: getShopSetting(
      userData,
      'feature_charge_item_code_disable',
      'enabled',
    ),
    featureAgingReport: getShopSetting(userData, 'feature_aging_report', 'message'),
    featureAgingReportPoNo: getShopSetting(userData, 'feature_aging_report_po_no', 'enabled'),
    featureBankReconciliation: getShopSetting(userData, 'feature_bank_reconciliation', 'message'),
    featureHideFirmCode: getShopSetting(userData, 'feature_hide_firm_code', 'enabled'),
    featureHideLocCode: getShopSetting(userData, 'feature_hide_loc_code', 'enabled'),
    featureHideCnStateCode: getShopSetting(userData, 'feature_hide_cn_state_code', 'enabled'),
    featureClassicShipmentAccountingTabOnly: getShopSetting(
      userData,
      'feature_classic_shipment_accounting_tab_only',
      'enabled',
    ),
    // opt-out, unlike every other feature_* here: the monthly FX revaluation shipped switched
    // on for every tenant, so an absent row means on and only enabled: false turns it off (#6367)
    featureFxRevaluation: getShopSetting(userData, 'feature_fx_revaluation', 'enabled') !== false,
    featureMultiOffice: getShopSetting(userData, 'feature_multi_offices', 'enabled'),
    featureMultiOfficesReport: getShopSetting(userData, 'feature_multi_offices_report', 'enabled'),
    featureAllowChangeShipmentARAPOffice: getShopSetting(
      userData,
      'feature_allow_change_shipment_arap_office',
      'enabled',
    ),
    featureDepartment: getShopSetting(userData, 'feature_department', 'enabled'),
    featureLocalEDIAllowAllOffices: getShopSetting(
      userData,
      'feature_local_edi_allow_all_offices',
      'enabled',
    ),
    featureLocalEDIMultipleOffices: getShopSetting(
      userData,
      'feature_local_edi_multiple_offices',
      'enabled',
    ),
    featureFullChargeItemTemplate: getShopSetting(
      userData,
      'feature_full_chargeitem_template',
      'enabled',
    ),
    featureRestrictCreateChargeItemTemplate: getShopSetting(
      userData,
      'feature_restrict_create_charge_item_template',
      'enabled',
    ),
    featureISFSuggestion: getShopSetting(userData, 'feature_isf_suggestion', 'enabled'),
    featureISF: getShopSetting(userData, 'feature_isf', 'enabled'),
    featureAMS: getShopSetting(userData, 'feature_ams', 'enabled'),
    featureACI: getShopSetting(userData, 'feature_aci', 'enabled'),
    featureAWBNumber: getShopSetting(userData, 'feature_awb_number', 'enabled'),
    featureMultiLanguage: getShopSetting(userData, 'feature_multi_language', 'enabled'),
    featureApprovalCenter: getShopSetting(userData, 'feature_approval', 'enabled'),
    featureContactMemoAutoLoad: getShopSetting(
      userData,
      'feature_contact_memo_auto_load',
      'enabled',
    ),
    featureTaxCodes: getShopSetting(userData, 'feature_tax_codes', 'enabled'),
    featureEditTaxAmount: getShopSetting(userData, 'feature_edit_tax_amount', 'enabled'),
    featureTaxInclusivePrice: getShopSetting(userData, 'feature_tax_inclusive_price', 'enabled'),
    featureWHT: getShopSetting(userData, 'feature_wht', 'enabled'),
    featureAllowChangeInvCode: getShopSetting(userData, 'feature_allow_change_inv_code', 'enabled'),
    featureShipmentReleaseApproval: getShopSetting(
      userData,
      'feature_shipment_release_approval',
      'enabled',
    ),
    featureShipmentLockApproval: getShopSetting(
      userData,
      'feature_shipment_lock_approval',
      'enabled',
    ),
    featureOceanImportBLInstruction:
      getShopSetting(userData, 'feature_ocean_import_bl_instruction', 'enabled') === undefined
        ? false
        : getShopSetting(userData, 'feature_ocean_import_bl_instruction', 'enabled'),
    featureSimpleTransaction: getShopSetting(userData, 'feature_simple_transaction', 'enabled'),
    featurePendingTransactions: getShopSetting(userData, 'feature_pending_transactions', 'enabled'),
    featureTransactionDepositDate: getShopSetting(
      userData,
      'feature_transaction_deposit_date',
      'enabled',
    ),
    featureDefaultEmptyDepositDate: getShopSetting(
      userData,
      'feature_default_empty_deposit_date',
      'enabled',
    ),
    featureExcelTemplateDownload: getShopSetting(
      userData,
      'feature_excel_template_download',
      'enabled',
    ),
    featureEmailFromUserAccount: getShopSetting(
      userData,
      'feature_email_from_user_account',
      'enabled',
    ),
    featureANDOEmailSubjectAdditions: getShopSetting(
      userData,
      'feature_ando_email_subject_additions',
      'message',
    ),
    featureDefaultEmptyCC: getShopSetting(userData, 'feature_default_empty_cc', 'enabled'),
    featureBillDependents: getShopSetting(userData, 'feature_bill_dependents', 'enabled'),
    featureCreditNoteDependents: getShopSetting(
      userData,
      'feature_credit_note_dependents',
      'enabled',
    ),
    featureDisableContactQuickAdd: getShopSetting(
      userData,
      'feature_disable_contact_quick_add',
      'enabled',
    ),
    featureTempContactQuickAdd: getShopSetting(
      userData,
      'feature_temp_contact_quick_add',
      'enabled',
    ),
    featureDisableContactWarning: getShopSetting(
      userData,
      'feature_disable_contact_warning',
      'enabled',
    ),
    featureRestrictedAccountingDate: getShopSetting(
      userData,
      'feature_restricted_accounting_date',
      'enabled',
    ),
    featureDailyCashReport: getShopSetting(userData, 'feature_daily_cash_report', 'enabled'),
    featureCurrentAccountReport: getShopSetting(
      userData,
      'feature_current_account_report',
      'enabled',
    ),
    featureTieInvoiceDateToEtaEtd: getShopSetting(
      userData,
      'feature_tie_invoice_date_to_eta_etd',
      'enabled',
    ),
    featureDueDateFromCreationDate: getShopSetting(
      userData,
      'feature_due_date_from_creation_date',
      'enabled',
    ),
    featureReportUseShipmentPostDate: getShopSetting(
      userData,
      'feature_report_use_shipment_post_date',
      'enabled',
    ),
    featureContainerLWH: getShopSetting(userData, 'feature_container_lwh', 'enabled'),
    featureMiscUseETD: getShopSetting(userData, 'feature_misc_use_etd', 'enabled'),
    featureSeparateETAETD: getShopSetting(userData, 'feature_separate_eta_etd', 'enabled'),
    featureAlwaysOpenNewTab: getShopSetting(userData, 'feature_always_open_new_tab', 'enabled'),
    featureSameDayDueDate: getShopSetting(userData, 'feature_same_day_due_date', 'enabled'),
    featureFapiao: getShopSetting(userData, 'feature_fapiao', 'enabled'),
    featureFapiaoForPayable: getShopSetting(userData, 'feature_fapiao_for_payable', 'enabled'),
    featureFapiaoRequest: getShopSetting(userData, 'feature_fapiao_request', 'enabled'),
    featureFapiaoRequestMessage: getShopSetting(userData, 'feature_fapiao_request', 'message'),
    featureSeparateARAPList: getShopSetting(userData, 'feature_separate_arap_list', 'enabled'),
    featurePaymentPlans: getShopSetting(userData, 'feature_payment_plans', 'enabled'),
    featurePaymentPlanPagination: getShopSetting(
      userData,
      'feature_payment_plan_pagination',
      'enabled',
    ),
    featureDoNotShowAccountInfoInPPRemark: getShopSetting(
      userData,
      'feature_do_not_show_account_info_in_pp_remark',
      'enabled',
    ),
    featurePayablePaymentPlanPayableOnly: getShopSetting(
      userData,
      'feature_payable_payment_plan_payable_only',
      'enabled',
    ),
    featureIncludeCustomerInStatement: getShopSetting(
      userData,
      'feature_include_customer_in_statement',
      'enabled',
    ),
    featureTableToolbarAffix: getShopSetting(userData, 'feature_table_toolbar_affix', 'enabled'),
    featureIncludeAllShipmentCustomersInStatement: getShopSetting(
      userData,
      'feature_include_all_shipment_customers_in_statement',
      'enabled',
    ),
    featureIncludeAllShipmentColoadersInStatement: getShopSetting(
      userData,
      'feature_include_all_shipment_coloaders_in_statement',
      'enabled',
    ),
    featureNotification: getShopSetting(userData, 'feature_notification', 'enabled'),
    featureCheckVersion:
      getShopSetting(userData, 'feature_check_version', 'enabled') === undefined
        ? true // if there is no such a setting, the default value
        : getShopSetting(userData, 'feature_check_version', 'enabled'),
    featureSubmitStatement: getShopSetting(userData, 'feature_submit_statement', 'enabled'),
    featureARAPDirectPayment: getShopSetting(userData, 'feature_arap_direct_payment', 'enabled'),
    featureInvoiceNow: getShopSetting(userData, 'feature_invoice_now', 'enabled'),
    featureBiller: getShopSetting(userData, 'feature_biller', 'enabled'),
    featureEfreightAms: getShopSetting(userData, 'feature_efreight_ams', 'enabled'),
    featureEfreightIsf: getShopSetting(userData, 'feature_efreight_isf', 'enabled'),
    featureSimpleShipment: getShopSetting(userData, 'feature_simple_shipment', 'enabled'),
    featureE2EExportShipment: getShopSetting(userData, 'feature_e2e_export_shipment', 'enabled'),
    featureFlyinUI: getShopSetting(userData, 'feature_flyin_ui', 'enabled'),
    featureEDIImport: getShopSetting(userData, 'feature_edi_import', 'enabled'),
    featureImport: getShopSetting(userData, 'feature_import', 'enabled'),
    featureTracking: getShopSetting(userData, 'feature_tracking', 'enabled'),
    featureTrackingManual: getShopSetting(userData, 'feature_tracking_manual', 'enabled'),
    featureTrackingSaveWarning: getShopSetting(
      userData,
      'feature_tracking_save_warning',
      'enabled',
    ),
    featureCargoManifest: getShopSetting(userData, 'feature_cargo_manifest', 'enabled'),
    featureWms: getShopSetting(userData, 'feature_wms', 'enabled'),
    featureSideMenuTrigger: getShopSetting(userData, 'feature_side_menu_trigger', 'enabled'),
    featureTodoGroupByShipment: getShopSetting(
      userData,
      'feature_todo_group_by_shipment',
      'enabled',
    ),
    featureLegacyTransactionReconciliation: getShopSetting(
      userData,
      'feature_legacy_transaction_reconciliation',
      'enabled',
    ),
    featureBlockedLogin: getShopSetting(userData, 'feature_blocked_login', 'enabled'),
    featureQuotation: getShopSetting(userData, 'feature_quotation', 'enabled'),
    featureQuotationApproval: getShopSetting(userData, 'feature_quotation_approval', 'enabled'),
    featureQtoShipment: getShopSetting(userData, 'feature_qtoshipment', 'enabled'),
    featureContainerCost: getShopSetting(userData, 'feature_container_cost', 'enabled'),
    featureFlipNotesIncomeExpense: getShopSetting(userData, 'flip_notes_income_expense', 'enabled'),
    featureStayWithDetail: getShopSetting(userData, 'feature_stay_with_detail', 'enabled'),
    featureStatementDocumentOverride: getShopSetting(
      userData,
      'feature_statement_document_override',
      'message',
    ),
    featureMilestoneShipmentReport: getShopSetting(
      userData,
      'feature_milestone_shipment_report',
      'enabled',
    ),
    featureAutoReply: getShopSetting(userData, 'feature_auto_reply', 'enabled'),
    featureDeliveryInfo: getShopSetting(userData, 'feature_delivery_info', 'enabled'),
    featureNewItemInbox:
      getShopSetting(userData, 'feature_new_item_inbox', 'enabled') === undefined
        ? true
        : getShopSetting(userData, 'feature_new_item_inbox', 'enabled'),
    featurePDFAutoResizing: getShopSetting(userData, 'feature_pdf_auto_resizing', 'enabled'),
    featureInputNumberControls:
      getShopSetting(userData, 'feature_input_number_controls', 'enabled') === undefined
        ? false
        : getShopSetting(userData, 'feature_input_number_controls', 'enabled'),
    monthlyClosing: getShopSetting(userData, 'feature_monthly_closing', 'message'),
    glCodeMapping: getShopSetting(userData, 'gl_code_mapping', 'message'),
    importMapping: getShopSetting(userData, 'feature_import', 'message'),
    todoSettings: getShopSetting(userData, 'todo_instructions', 'message'),
    listSettings: getShopSetting(userData, 'feature_lists_settings', 'message') || {},
    featureOverrideOptions: getShopSetting(userData, 'feature_override_options', 'message') || {},
    featureDocumentTypes: getShopSetting(userData, 'feature_document_types', 'message') || [],
    agingReportDueDateField: getShopSetting(userData, 'feature_aging_report_date_field', 'enabled'),
    viewAccounting:
      userData?.shop_roles?.includes(UserRoles.admin) ||
      userData?.shop_roles?.includes(UserRoles.view_accounting),
    viewReport:
      userData?.shop_roles?.includes(UserRoles.admin) ||
      userData?.shop_roles?.includes(UserRoles.view_report),
    availableCurrencies: userData?.shop?.currencies || [],
    featureIncludeInvoicePdfInStatement: getShopSetting(
      userData,
      'feature_include_invoice_pdf_in_statement',
      'enabled',
    ),
    featureEDI: getShopSetting(userData, 'feature_edi', 'enabled'),
    // set the featureTruck default to true, unless setting is disabled
    featureTruck:
      getShopSetting(userData, 'feature_truck', 'enabled') === undefined
        ? true
        : getShopSetting(userData, 'feature_truck', 'enabled'),

    featureYearEndClosing: getShopSetting(userData, 'feature_year_end_closing', 'enabled'),
    featureCopyLink: getShopSetting(userData, 'feature_copy_link', 'enabled'),
    // set the featureShowVersionWarning default to true, unless setting is disabled
    featureShowVersionWarning:
      getShopSetting(userData, 'feature_show_version_warning', 'enabled') === undefined
        ? false // by default, we don't show the version warning, this caused the profile cannot be opened
        : getShopSetting(userData, 'feature_show_version_warning', 'enabled'),
    featureOldChargeItemsUI: getShopSetting(userData, 'feature_old_charge_items_ui', 'enabled'),
    featureNewContainerUI: getShopSetting(userData, 'feature_new_container_ui', 'enabled'),
    featureDuplicateReferenceWarning: getShopSetting(
      userData,
      'feature_duplicate_reference_warning',
      'enabled',
    ),
    featureDuplicateReferenceWarningKinds: getShopSetting(
      userData,
      'feature_duplicate_reference_warning',
      'message',
    ),
    featureShipmentMemo: getShopSetting(userData, 'feature_shipment_memo', 'enabled'),
    featureShipmentMemoTypes: getShopSetting(userData, 'feature_shipment_memo', 'message'),
    featureMyContainerList: getShopSetting(userData, 'feature_my_container_list', 'enabled'),
    featureLockShipmentNumber: getShopSetting(userData, 'feature_lock_shipment_number', 'enabled'),
    featureCustomerPortal: getShopSetting(userData, 'feature_customer_portal', 'enabled'),
    featureCustomerPortalInvoice: getShopSetting(
      userData,
      'feature_customer_portal_invoice',
      'enabled',
    ),
    customerPortalInvoice:
      userData?.type === UserType.customer &&
      getShopSetting(userData, 'feature_customer_portal', 'enabled') === true &&
      getShopSetting(userData, 'feature_customer_portal_invoice', 'enabled') === true,
    featureDefaultAutoGenerateHblNo: getShopSetting(
      userData,
      'feature_default_auto_generate_hbl_no',
      'enabled',
    ),
    featureRequireDirectMasterIfNoHbl: getShopSetting(
      userData,
      'feature_require_direct_master_if_no_hbl',
      'enabled',
    ),
    featureExternalCurrencyRate: getShopSetting(
      userData,
      'feature_external_currency_rate',
      'enabled',
    ),
    featureShowSalesTypeMBL: getShopSetting(userData, 'feature_show_sales_type_mbl', 'enabled'),
    featureDefaultDashboard: getShopSetting(userData, 'feature_default_dashboard', 'message'),
    featureCustomUnits: getShopSetting(userData, 'feature_custom_units', 'message'),
    featureExcludeItemsInPNL: getShopSetting(userData, 'feature_exclude_items_in_pnl', 'message'),
    featureGSTPayment: getShopSetting(userData, 'feature_gst_payment', 'enabled'),
    featureCombinedTaxReport: getShopSetting(userData, 'feature_combined_tax_report', 'enabled'),
    featureProfitMarginPercentage: getShopSetting(
      userData,
      'feature_profit_margin_percentage',
      'enabled',
    ),
    featurePDFLastModifiedAsDefaultSource: getShopSetting(
      userData,
      'feature_pdf_last_modified_as_default_source',
      'enabled',
    ),
    featureAsyncPDFEmail: getShopSetting(userData, 'feature_async_pdf_email', 'enabled'),
    featureARAPPostDate: getShopSetting(userData, 'feature_arap_post_date', 'enabled'),
    featureManualEditARAPPostDate: getShopSetting(
      userData,
      'feature_manual_edit_arap_post_date',
      'enabled',
    ),
    featureTableFixedHeader: getShopSetting(userData, 'feature_table_fixed_header', 'enabled'),
    featureGroupMenu: getShopSetting(userData, 'feature_group_menu', 'enabled'),
    featureNoBlockedDateRestrictions: getShopSetting(
      userData,
      'feature_no_blocked_date_restrictions',
      'enabled',
    ),
    featureCollapseOnQuery:
      getShopSetting(userData, 'feature_collapse_on_query', 'enabled') === undefined
        ? true // if there is no such a setting, the default value
        : getShopSetting(userData, 'feature_collapse_on_query', 'enabled'),
    // featureChargeItemsLoadFromARAP: getShopSetting(
    //   userData,
    //   'feature_charge_items_load_from_arap',
    //   'enabled',
    // ),
    // by default, we enable load from ar ap
    featureChargeItemsLoadFromARAP: true,
    featureDownloadReconciliation: getShopSetting(
      userData,
      'feature_download_reconciliation',
      'enabled',
    ),
    featureAutoDO: getShopSetting(userData, 'feature_auto_do', 'enabled'),
    featureChargeItemsTemplateCalculation: getShopSetting(
      userData,
      'feature_charge_items_template_calculation',
      'enabled',
    ),
    featureDraftChargeItems: getShopSetting(userData, 'feature_draft_charge_items', 'enabled'),
    featureCheckboxInAmountColumn: getShopSetting(
      userData,
      'feature_checkbox_in_amount_column',
      'enabled',
    ),
    shipmentAISettings: getShopSetting(userData, 'ai_settings_shipment', 'message'),
    featureLoadFromARAPWithDescription:
      getShopSetting(
        userData,
        'feature_charge_items_load_from_arap_with_description',
        'enabled',
      ) === undefined
        ? true // by default, we load from ar ap
        : getShopSetting(
            userData,
            'feature_charge_items_load_from_arap_with_description',
            'enabled',
          ),
    featureGFStyleGlobalSearch: getShopSetting(userData, 'feature_gf_global_search', 'enabled'),
    featureOpenContainer:
      getShopSetting(userData, 'feature_open_container', 'enabled') === undefined
        ? false // by default, it's off
        : getShopSetting(userData, 'feature_open_container', 'enabled'),
    featureDisableContainerSumsEdit: getShopSetting(
      userData,
      'feature_disable_container_sums_edit',
      'enabled',
    ),
    featureShowMoreFields: getShopSetting(userData, 'feature_show_more_fields', 'enabled'),
    featureExtendedWeightFields: getShopSetting(
      userData,
      'feature_extended_weight_fields',
      'enabled',
    ),
    featureSalesReportByHbl: getShopSetting(userData, 'feature_sales_report_by_hbl', 'enabled'),
    // access control
    ...permissions,
    account_reports_or_aging_report:
      userData?.shop_roles?.includes(UserRoles.admin) ||
      userData?.shop_roles?.includes(UserRoles.account_aging_report) ||
      userData?.shop_roles?.includes(UserRoles.account_reports) ||
      userData?.shop_roles?.includes(UserRoles.view_report),
    gl_codes_access:
      userData?.shop_roles?.includes(UserRoles.admin) ||
      userData?.shop_roles?.includes(UserRoles.gl_codes),
    featureAutoAssignOperator: getShopSetting(userData, 'feature_auto_assign_operator', 'enabled'),
    featureLimitedContactAdd: getShopSetting(userData, 'feature_limited_contact_add', 'enabled'),
    featureContactEmail: getShopSetting(userData, 'feature_contact_email', 'enabled'),
    featureContactCodeBeforeName: getShopSetting(
      userData,
      'feature_contact_code_before_name',
      'enabled',
    ),
    featureContactShowTypeInSelect:
      getShopSetting(userData, 'feature_contact_show_type_in_select', 'enabled') === true,
    featureCapitalization: getShopSetting(userData, 'feature_capitalization', 'enabled'),
    featureMOMBalanceSheet: getShopSetting(userData, 'feature_mom_balance_sheet', 'enabled'),
    featureLockPermUserRole: getShopSetting(userData, 'feature_lock_perm_user_role', 'enabled'),
    featureSequentialChargeItemCode: getShopSetting(
      userData,
      'feature_sequential_charge_item_code',
      'enabled',
    ),
    featureNoNewARAPAfterPermLock: getShopSetting(
      userData,
      'feature_no_new_arap_after_perm_lock',
      'enabled',
    ),
    featureDisableExpandMyShipments: getShopSetting(
      userData,
      'feature_disable_expand_my_shipments',
      'enabled',
    ),
    featureDocumentCenterDragUpload: getShopSetting(
      userData,
      'feature_document_center_drag_upload',
      'enabled',
    ),
    featureQuotationReportShowLocalName: getShopSetting(
      userData,
      'feature_quotation_report_show_local_name',
      'enabled',
    ),
    featureAddARAPInTransaction: getShopSetting(
      userData,
      'feature_add_arap_in_transaction',
      'enabled',
    ),
    featureAddARAPInTransactionWithStatementModal: getShopSetting(
      userData,
      'feature_add_arap_in_transaction_with_statement_modal',
      'enabled',
    ),
    featureCopyStatementDocumentsToTransaction: getShopSetting(
      userData,
      'feature_copy_statement_documents_to_transaction',
      'enabled',
    ),
    featureCRM: getShopSetting(userData, 'feature_crm', 'enabled'),
    featureShareCRM: getShopSetting(userData, 'feature_share_crm', 'enabled'),
    featureShareCRMSettings: getShopSetting(userData, 'feature_share_crm', 'message') || {},
    featureENS: getShopSetting(userData, 'feature_ens', 'enabled'),
    featureAES: getShopSetting(userData, 'feature_aes', 'enabled'),
    featureAFR: getShopSetting(userData, 'feature_afr', 'enabled'),
    featureRateManagement: getShopSetting(userData, 'feature_rate_management', 'enabled'),
    rateManagementSettings: getShopSetting(userData, 'feature_rate_management', 'message') || {},
    featureNoQuotationCostGroup: getShopSetting(
      userData,
      'feature_no_quotation_cost_group',
      'enabled',
    ),
    featureContactDefaultUSA: getShopSetting(userData, 'feature_contact_default_usa', 'enabled'),
    featureDisableOwnerDeleteShipment: getShopSetting(
      userData,
      'feature_disable_owner_delete_shipment',
      'enabled',
    ),
    featureManagePorts: getShopSetting(userData, 'feature_manage_ports', 'enabled') ?? true,
    featureTodosChart: getShopSetting(userData, 'feature_todos_chart', 'enabled'),
    featureTodosList: !getShopSetting(userData, 'feature_todos_chart', 'enabled'),
    featureShowContactCredit: getShopSetting(userData, 'feature_show_contact_credit', 'enabled'),
    featureAllowAllMembersForCoOp: getShopSetting(
      userData,
      'feature_allow_all_members_for_co_op',
      'enabled',
    ),
    featureContactCopyCompleteInfo: getShopSetting(
      userData,
      'feature_contact_copy_complete_info',
      'enabled',
    ),
    featureIncludeContainersInStatement: getShopSetting(
      userData,
      'feature_include_containers_in_statement',
      'enabled',
    ),
    featureShowItemWithTooltip: getShopSetting(
      userData,
      'feature_show_item_with_tooltip',
      'enabled',
    ),
    featureDefaultTransactionsToBankReconciliation: getShopSetting(
      userData,
      'feature_default_transactions_to_bank_reconciliation',
      'message',
    ),
    featureMenuOverride: getShopSetting(userData, 'feature_menu_override', 'message'),
    featureShowCancelledShipmentCount: getShopSetting(
      userData,
      'feature_show_cancelled_shipment_count',
      'enabled',
    ),
    featureShowShipmentARAPInStatement: getShopSetting(
      userData,
      'feature_show_shipment_arap_in_statement',
      'enabled',
    ),
    featureShowCarrierBookingNumberInStatement: getShopSetting(
      userData,
      'feature_show_carrier_booking_number_in_statement',
      'enabled',
    ),
    featureIncludeAllShipmentHblsInStatement: getShopSetting(
      userData,
      'feature_include_all_shipment_hbls_in_statement',
      'enabled',
    ),
    featureStatementContainerNoList: getShopSetting(
      userData,
      'feature_statement_container_no_list',
      'enabled',
    ),
    featureStatementsManualSearch: getShopSetting(
      userData,
      'feature_statements_manual_search',
      'enabled',
    ),
    featureShowSalesInStatementApproval: getShopSetting(
      userData,
      'feature_show_sales_in_statement_approval',
      'enabled',
    ),
    tableStyleSettings:
      getShopSetting(userData, 'feature_table_condition_classname', 'message') || {},
    featureIncludeAllShipmentContainersInStatement: getShopSetting(
      userData,
      'feature_include_all_shipment_containers_in_statement',
      'enabled',
    ),
    featureContract: getShopSetting(userData, 'feature_contract', 'enabled'),
    featureMergeContactRole: getShopSetting(userData, 'feature_merge_contact_role', 'enabled'),
    featureContactExportRole,
    canExportContacts:
      !featureContactExportRole ||
      userData?.shop_roles?.includes(UserRoles.admin) ||
      !!permissions[UserRoles.export_contact_excel]?.(),
    featureInboxAttachments: getShopSetting(userData, 'feature_inbox_attachments', 'enabled'),
    featureHideBookingMenu: getShopSetting(userData, 'feature_hide_booking_menu', 'enabled'),
    featureBooking: getShopSetting(userData, 'feature_booking', 'enabled'),
    featureShowOverdueTodoOnly: getShopSetting(
      userData,
      'feature_show_overdue_todo_only',
      'enabled',
    ),
    formAutoFormatSettings: getShopSetting(userData, 'feature_form_auto_format', 'message') || {},
    featureShowTracingReport: getShopSetting(userData, 'feature_show_tracing_report', 'enabled'),
    dateFormats: getShopSetting(userData, 'feature_date_format', 'message') || {},
    featureSeperateETAETDReport: getShopSetting(
      userData,
      'feature_seperate_eta_etd_report',
      'enabled',
    ),
    featureNewStatementInTab: getShopSetting(userData, 'feature_new_statement_in_tab', 'enabled'),
    featureShowMainCurrencyOnlyInShipmentAccounting: getShopSetting(
      userData,
      'feature_show_main_currency_only_in_shipment_accounting',
      'enabled',
    ),
    featureShowActualProfitInShipmentReport: getShopSetting(
      userData,
      'feature_show_actual_profit_in_shipment_report',
      'enabled',
    ),
    featureRestrictContactType: getShopSetting(
      userData,
      'feature_restrict_contact_type',
      'enabled',
    ),
    featureContactMinisearch: getShopSetting(userData, 'feature_contact_minisearch', 'enabled'),
    featurePinnableMenu: getShopSetting(userData, 'feature_pinnable_menu', 'enabled'),
    // featureShowShipmentAccountingDetails: getShopSetting(
    //   userData,
    //   'feature_show_shipment_accounting_details',
    //   'enabled',
    // ),
    featureShowShipmentAccountingDetails: true, // enable by default
    featureProfitShareARAP: getShopSetting(userData, 'feature_profit_share_arap', 'enabled'),
    featureShowLabelsSeparately:
      getShopSetting(userData, 'feature_show_labels_separately', 'enabled') === false
        ? false
        : true, // enable by default
    featureShowHblCustomerLabels: !!getShopSetting(
      userData,
      'feature_show_hbl_customer_labels',
      'enabled',
    ),
    featureNotShowOverdueTodo: getShopSetting(userData, 'feature_not_show_overdue_todo', 'enabled'),
    featureVendorCreditNote: getShopSetting(userData, 'feature_vendor_credit_note', 'enabled'),
    featureARAPShowContactLocalName: getShopSetting(
      userData,
      'feature_arap_show_contact_local_name',
      'enabled',
    ),
    featureDoNotAutoQueryOpenInvoices: getShopSetting(
      userData,
      'feature_do_not_auto_query_open_invoices',
      'enabled',
    ),
    featureNoFreeTextInPortSelect: getShopSetting(
      userData,
      'feature_no_free_text_in_port_select',
      'enabled',
    ),
    featureExcludeMBLDocumentsInHBLEmailModal: getShopSetting(
      userData,
      'feature_exclude_mbl_documents_in_hbl_email_modal',
      'enabled',
    ),
    featureMcpKeys:
      getShopSetting(userData, 'feature_mcp_keys', 'enabled') &&
      (!getShopSetting(userData, 'feature_mcp_keys_role_required', 'enabled') ||
        userData?.shop_roles?.includes(UserRoles.admin) ||
        userData?.shop_roles?.includes(UserRoles.access_mcp)),
  };
}
